import React from 'react';
import { getPmsToolbarIcon } from '../shared/pmsToolbarIcon';
import { createPmsGetTableActions } from '../shared/pmsTableActions';
import { pmsRecordStatusField, pmsVesselField } from '../shared/pmsFormFields';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { PmsTool } from './useData';

function stripToolPayload(data: Partial<PmsTool>): Record<string, unknown> {
  const {
    vessel,
    recordStatus,
    jobLinks,
    measurements,
    id,
    createdAt,
    updatedAt,
    createdByUserId,
    updatedByUserId,
    ...payload
  } = data as Record<string, unknown>;
  return payload;
}

function toIsoDate(value: unknown): string | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'string') return value.slice(0, 10);
  return undefined;
}

export const moduleConfig = {
  module: {
    title: 'Tools',
    permissionModuleKey: 'pms_tools',
    route: {
      list: '/pms/pms-tools',
      new: '/pms/pms-tools/new',
      detail: (id: number) => `/pms/pms-tools/${id}`,
      edit: (id: number) => `/pms/pms-tools/${id}/edit`},
    api: { list: '/pms-tools', detail: (id: number) => `/pms-tools/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: PmsTool[]) => items ,
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New',
        showTitle: false,
        variant: 'primary' as const,
        href: '/pms/pms-tools/new'},
    ]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: PmsTool) => item.id,
  },
  emptyStates: {
    noData: 'No records.',
    noSearchResults: 'No matches.',
    noFilterResults: 'No matches.',
  },
  delete: {
    title: 'Delete',
    message: 'Delete this record?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deletePmsTool } = await import('../../../services/mutations');
      await deletePmsTool(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Not found.',
    notFoundBackText: 'Back',
    defaultTitle: 'Tools',
    getTitle: (item: PmsTool) =>
      item.name ? String(item.name) : item.code ? String(item.code) : `#${item.id}`},
  form: {
    new: { title: 'New tool', submitText: 'Create' },
    edit: { title: 'Edit tool', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formMaxWidth: 'max-w-4xl',
    create: async (data: Partial<PmsTool>) => {
      const { createPmsTool } = await import('../../../services/mutations');
      const payload = stripToolPayload(data);
      if (payload.calibrationExpiryDate != null && payload.calibrationExpiryDate !== '') {
        payload.calibrationExpiryDate = toIsoDate(payload.calibrationExpiryDate);
      } else {
        delete payload.calibrationExpiryDate;
      }
      if (payload.vesselId === '' || payload.vesselId == null) {
        delete payload.vesselId;
      }
      return createPmsTool(payload);
    },
    update: async (id: number, data: Partial<PmsTool>) => {
      const { updatePmsTool } = await import('../../../services/mutations');
      const payload = stripToolPayload(data);
      if (payload.calibrationExpiryDate != null && payload.calibrationExpiryDate !== '') {
        payload.calibrationExpiryDate = toIsoDate(payload.calibrationExpiryDate);
      } else if (!data.calibrationRequired) {
        payload.calibrationExpiryDate = null;
      }
      if (payload.vesselId === '' || payload.vesselId == null) {
        payload.vesselId = null;
      }
      return updatePmsTool(id, payload);
    }},
  fields: [
    {
      ...pmsVesselField(false),
      required: false,
      clearable: true,
      placeholder: 'None — shared / office tool',
      helperText: 'Optional. Leave empty for tools not tied to a single vessel'},
    {
      ...pmsRecordStatusField(true),
      helperText: 'Lifecycle state of this tool record'},
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'e.g. TOOL-TORQUE-01',
      validation: { required: true },
      helperText: 'Unique per vessel (or globally when vessel is empty)'},
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. Torque wrench 0–200 Nm',
      validation: { required: true }},
    {
      name: 'toolType',
      label: 'Tool type',
      type: 'text',
      placeholder: 'e.g. Torque wrench, Micrometer, Multimeter'},
    {
      name: 'serialNumber',
      label: 'Serial number',
      type: 'text',
      placeholder: 'Maker serial if available'},
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      rows: 3,
      placeholder: 'Usage notes, range, and storage location'},
    {
      name: 'calibrationRequired',
      label: 'Calibration',
      type: 'switch',
      switchLabel: 'Calibration required'},
    {
      name: 'calibrationExpiryDate',
      label: 'Calibration expiry',
      type: 'date',
      helperText: 'Next due date for calibration',
      validation: {
        requiredWhen: (fd) => !!fd.calibrationRequired,
        message: 'Enter calibration expiry when calibration is required'}},
    {
      name: 'isActive',
      label: 'Status',
      type: 'switch',
      switchLabel: 'Tool is active'},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'scope',
        title: 'Scope',
        description: 'Optional vessel ownership and record lifecycle.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['vesselId', 'recordStatusId'],
        fieldSpans: {
          vesselId: 'md:col-span-6',
          recordStatusId: 'md:col-span-6'},
        collapsible: false},
      {
        id: 'identity',
        title: 'Tool',
        description: 'Identity used when linking tools to jobs and measurements.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['code', 'name', 'toolType', 'serialNumber', 'description'],
        fieldSpans: {
          code: 'md:col-span-4',
          name: 'md:col-span-8',
          toolType: 'md:col-span-6',
          serialNumber: 'md:col-span-6',
          description: 'md:col-span-12'},
        collapsible: false},
      {
        id: 'calibration',
        title: 'Calibration',
        description: 'Whether this instrument needs periodic calibration.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['calibrationRequired'],
        fieldSpans: { calibrationRequired: 'md:col-span-6' },
        collapsible: false},
      {
        id: 'calibration-expiry',
        title: 'Calibration expiry',
        description: 'Shown when calibration is required.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['calibrationExpiryDate'],
        fieldSpans: { calibrationExpiryDate: 'md:col-span-4' },
        collapsible: false,
        visibleWhen: (fd) => !!fd.calibrationRequired,
        visibilityDependsOn: ['calibrationRequired']},
      {
        id: 'status',
        title: 'Status',
        description: 'Whether the tool can be assigned to new work.',
        icon: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['isActive'],
        fieldSpans: { isActive: 'md:col-span-6' },
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const relName = (
  rel: { code?: string | null; name?: string | null; vesselName?: string | null } | null | undefined,
  id?: number | null,
) => {
  if (rel?.name) return rel.name;
  if (rel?.vesselName) return rel.vesselName;
  return rel?.code ?? (id != null ? `#${id}` : '-');
};

const labelVessel = (item: PmsTool) => {
  const v = item.vessel as { vesselName?: string; name?: string } | null | undefined;
  if (!v && item.vesselId == null) return 'Shared';
  return v?.vesselName ?? v?.name ?? (item.vesselId != null ? `#${item.vesselId}` : 'Shared');
};

export const getColumns = (_formatDate: (dateString?: string) => string): Column<PmsTool>[] => [
  { key: 'name', header: 'Name', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  { key: 'code', header: 'Code', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  {
    key: 'vesselId',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => <TextCell value={labelVessel(row)} />},
  {
    key: 'calibrationRequired',
    header: 'Calibration',
    sortable: true,
    render: (v) => <TextCell value={v ? 'Required' : 'No'} />},
  {
    key: 'isActive',
    header: 'Active',
    sortable: true,
    render: (v) => <TextCell value={v === false ? 'No' : 'Yes'} />},
];

export const getDetailSections = (item: PmsTool, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Vessel', value: labelVessel(item) },
      {
        label: 'Record status',
        value: relName(
          item.recordStatus as { code?: string; name?: string } | null,
          item.recordStatusId as number | null,
        )},
      { label: 'Code', value: item.code != null ? String(item.code) : '-' },
      { label: 'Name', value: item.name != null ? String(item.name) : '-' },
      { label: 'Description', value: item.description != null ? String(item.description) : '-' },
      { label: 'Tool type', value: item.toolType != null ? String(item.toolType) : '-' },
      { label: 'Serial number', value: item.serialNumber != null ? String(item.serialNumber) : '-' },
      { label: 'Calibration required', value: item.calibrationRequired ? 'Yes' : 'No' },
      {
        label: 'Calibration expiry',
        value: item.calibrationExpiryDate ? formatDate(String(item.calibrationExpiryDate)) : '-'},
      { label: 'Active', value: item.isActive !== false ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
    ]},
];

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<PmsTool> => ({
  vesselId: undefined,
  recordStatusId: undefined,
  code: '',
  name: '',
  description: '',
  toolType: '',
  serialNumber: '',
  calibrationRequired: false,
  calibrationExpiryDate: undefined,
  isActive: true});
export const mapItemToFormData = (item: PmsTool): Partial<PmsTool> => ({
  vesselId: item.vesselId != null ? Number(item.vesselId) : undefined,
  recordStatusId: item.recordStatusId != null ? Number(item.recordStatusId) : undefined,
  vessel: item.vessel ?? undefined,
  recordStatus: item.recordStatus ?? undefined,
  code: item.code != null ? String(item.code) : '',
  name: item.name != null ? String(item.name) : '',
  description: item.description != null ? String(item.description) : '',
  toolType: item.toolType != null ? String(item.toolType) : '',
  serialNumber: item.serialNumber != null ? String(item.serialNumber) : '',
  calibrationRequired: item.calibrationRequired ?? false,
  calibrationExpiryDate: item.calibrationExpiryDate
    ? String(item.calibrationExpiryDate).slice(0, 10)
    : undefined,
  isActive: item.isActive ?? true});
export const getToolbarIcon = getPmsToolbarIcon;
export const getTableActions = createPmsGetTableActions(moduleConfig.module.route);
